import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import OrderBook from './components/OrderBook';
import UserOrders from './components/UserOrders';
import PnL from './components/PnL';
import AdminPanel from './components/AdminPanel';
import Toast from './components/Toast';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';

function App() {
  const [socket, setSocket] = useState(null);
  const [status, setStatus] = useState('Disconnected');
  const [messages, setMessages] = useState([]);
  const [orderBooks, setOrderBooks] = useState({});
  const [activeProduct, setActiveProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [filledOrders, setFilledOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [pnl, setPnL] = useState(0);
  const [positions, setPositions] = useState({});
  const [averageCosts, setAverageCosts] = useState({});
  const [productPnL, setProductPnL] = useState({});
  const [orderForm, setOrderForm] = useState({
    side: '0',
    type: '1',
    price: '',
    quantity: '',
    product: ''
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [tradingEnabled, setTradingEnabled] = useState(true);
  const [currentUsername, setCurrentUsername] = useState(() => {
    const storedCredentials = Cookies.get('credentials');
    return storedCredentials ? JSON.parse(storedCredentials).username : '';
  });
  const [visibleProducts, setVisibleProducts] = useState([]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const connect = useCallback(() => {
    // 161.178.3.10
    const newSocket = new WebSocket('ws://ec2-161-178-3-10.compute-1.amazonaws.com:9002');
    // const newSocket = new WebSocket('ws://localhost:9002');

    newSocket.onopen = () => {
      // console.log("Connection established");
      setStatus('Connected');
      
      // Check for stored credentials and send them if they exist
      const storedCredentials = Cookies.get('credentials');
      if (storedCredentials) {
        const credentials = JSON.parse(storedCredentials);
        const authMessage = {
          type: 'auth',
          username: credentials.username,
          password: credentials.password
        };
        newSocket.send(JSON.stringify(authMessage));
      }
    };

    newSocket.onmessage = (event) => {
      // console.log("Received message:", JSON.parse(event.data));
      const data = JSON.parse(event.data);
      
      if (data.type === 'authRequest') {
        // Server is requesting authentication
        // console.log("Authentication requested");
      } else if (data.type === 'authSuccess') {
        setIsAuthenticated(true);
        setOrderBooks(data.orderBooks || {});
        setProducts(Object.keys(data.orderBooks || {}));
        // Don't set visible products here - wait for visibility messages
        const firstProduct = Object.keys(data.orderBooks || {})[0];
        if (firstProduct) {
          setActiveProduct(firstProduct);
        }
        if (data.userState) {
          setFilledOrders(data.userState.filledOrders || []);
          setPendingOrders(data.userState.pendingOrders || []);
          setPnL(data.userState.pnl || 0);
          setPositions(data.userState.positions || {});
          setAverageCosts(data.userState.averageCosts || {});
          setProductPnL(data.userState.productPnL || {});
        }
        // Update admin status and store in cookie
        setIsAdmin(data.isAdmin || false);
        if (data.isAdmin) {
          const credentials = JSON.parse(Cookies.get('credentials') || '{}');
          Cookies.set('credentials', JSON.stringify({
            ...credentials,
            isAdmin: true
          }), { expires: 7 });
        } else {
          setAdminPassword('');
        }
        // Update current username from cookies
        const storedCredentials = Cookies.get('credentials');
        if (storedCredentials) {
          setCurrentUsername(JSON.parse(storedCredentials).username);
        }
      } else if (data.type === 'error') {
        setMessages(prevMessages => [...prevMessages, `Error: ${data.message}`]);
        setToast({
          show: true,
          message: data.message,
          type: 'error'
        });
      } else if (data.type === 'orderConfirmation') {
        setToast({
          show: true,
          message: data.message,
          type: data.message.startsWith('Error') ? 'error' : 'success'
        });
      } else if (data.type === 'orderFilled') {
        // Only show fill toast if it's an immediate fill, not for pending orders
        if (!data.isPending) {
          setToast({
            show: true,
            message: `Order filled: ${data.side === 'BUY' ? 'Buy' : 'Sell'} ${data.quantity} ${data.product} @ $${data.price}`,
            type: 'success'
          });
        }
      } else if (data.type === 'orderBookDelta') {
        if (data.product) {
          setOrderBooks(prev => {
            const currentBook = prev[data.product] || { bids: [], asks: [] };
            const updatedBook = {
              ...currentBook,
              bids: [...currentBook.bids],
              asks: [...currentBook.asks]
            };

            // Apply the delta
            if (data.delta) {
              let bestBidChanged = false;
              let bestAskChanged = false;
              let newBestBid = updatedBook.bids[0]?.price;
              let newBestAsk = updatedBook.asks[0]?.price;

              if (data.delta.bids) {
                data.delta.bids.forEach(bid => {
                  const index = updatedBook.bids.findIndex(b => b.price === bid.price);
                  if (bid.quantity === 0 || bid.action === 'remove') {
                    if (index !== -1) {
                      updatedBook.bids.splice(index, 1);
                      if (index === 0) bestBidChanged = true;
                    }
                  } else if (index === -1) {
                    updatedBook.bids.push(bid);
                  } else {
                    updatedBook.bids[index] = bid;
                    if (index === 0) bestBidChanged = true;
                  }
                });
              }
              if (data.delta.asks) {
                data.delta.asks.forEach(ask => {
                  const index = updatedBook.asks.findIndex(a => a.price === ask.price);
                  if (ask.quantity === 0 || ask.action === 'remove') {
                    if (index !== -1) {
                      updatedBook.asks.splice(index, 1);
                      if (index === 0) bestAskChanged = true;
                    }
                  } else if (index === -1) {
                    updatedBook.asks.push(ask);
                  } else {
                    updatedBook.asks[index] = ask;
                    if (index === 0) bestAskChanged = true;
                  }
                });
              }

              // Sort the order book
              updatedBook.bids.sort((a, b) => b.price - a.price);
              updatedBook.asks.sort((a, b) => a.price - b.price);

              // Check if best prices changed after sorting
              if (updatedBook.bids[0]?.price !== newBestBid) bestBidChanged = true;
              if (updatedBook.asks[0]?.price !== newBestAsk) bestAskChanged = true;

              // Update market order price if needed
              if (data.product === activeProduct && orderForm.type === '0') {
                if (orderForm.side === '0' && bestAskChanged) { // Buy order - use lowest ask
                  setOrderForm(prev => ({
                    ...prev,
                    price: updatedBook.asks[0]?.price || ''
                  }));
                } else if (orderForm.side === '1' && bestBidChanged) { // Sell order - use highest bid
                  setOrderForm(prev => ({
                    ...prev,
                    price: updatedBook.bids[0]?.price || ''
                  }));
                }
              }
            }

            return {
              ...prev,
              [data.product]: updatedBook
            };
          });
        }
      } else if (data.type === 'userUpdate') {
        setFilledOrders(data.filledOrders || []);
        setPendingOrders(data.pendingOrders || []);
        setPnL(data.pnl || 0);
        setPositions(data.positions || {});
        setAverageCosts(data.averageCosts || {});
        
        // Show toast for new fills
        if (data.filledOrders && data.filledOrders.length > filledOrders.length) {
          const newFills = data.filledOrders.slice(filledOrders.length);
          newFills.forEach(fill => {
            setToast({
              show: true,
              message: `Order filled: ${fill.side === 'BUY' ? 'Buy' : 'Sell'} ${fill.quantity} ${fill.product} @ $${fill.price}`,
              type: 'success'
            });
          });
        }
      } else if (data.type === 'userUpdateDelta') {
        // Handle delta updates for user information
        const delta = data.delta;
        
        // Update PnL if changed
        if (delta.pnl !== undefined) {
          setPnL(delta.pnl);
        }
        
        // Update pending orders if changed
        if (delta.pendingOrders) {
          setPendingOrders(prevOrders => {
            let updatedOrders = [...prevOrders];
            
            delta.pendingOrders.forEach(change => {
              if (change.action === 'add') {
                // Add the new order
                updatedOrders.push(change.order);
              } else if (change.action === 'update') {
                // Find and update the order
                const index = updatedOrders.findIndex(order => order.id === change.order.id);
                if (index !== -1) {
                  updatedOrders[index] = change.order;
                }
              } else if (change.action === 'remove') {
                // Remove the order
                updatedOrders = updatedOrders.filter(order => order.id !== change.orderId);
              }
            });
            
            return updatedOrders;
          });
        }
        
        // Update filled orders if changed
        if (delta.filledOrders) {
          setFilledOrders(prevOrders => {
            let updatedOrders = [...prevOrders];
            
            delta.filledOrders.forEach(change => {
              if (change.action === 'add') {
                // Add the new filled order
                updatedOrders.push(change.order);
                
                // Remove the filled order from pending orders
                setPendingOrders(prevPendingOrders => 
                  prevPendingOrders.filter(order => order.id !== change.order.id)
                );
                
                // Show toast for new fill
                setToast({
                  show: true,
                  message: `Order filled: ${change.order.side === 'BUY' ? 'Buy' : 'Sell'} ${change.order.quantity} ${change.order.product} @ $${change.order.price}`,
                  type: 'success'
                });
              }
            });
            
            return updatedOrders;
          });
        }
        
        // Update positions if changed
        if (delta.positions) {
          setPositions(prevPositions => {
            return { ...prevPositions, ...delta.positions };
          });
        }
        
        // Update average costs if changed
        if (delta.averageCosts) {
          setAverageCosts(prevCosts => {
            return { ...prevCosts, ...delta.averageCosts };
          });
        }
      } else if (data.type === 'message') {
        setMessages(prevMessages => [...prevMessages, `**${data.content}**`]);
        setToast({
          show: true,
          message: data.content,
          type: 'info'
        });
      } else if (data.type === 'adminMessage') {
        setMessages(prevMessages => [...prevMessages, `**ADMIN MESSAGE: ${data.content}**`]);
        setToast({
          show: true,
          message: data.content,
          type: 'info'
        });
      } else if (data.type === 'tradingStatus') {
        setTradingEnabled(data.enabled);
        setToast({
          show: true,
          message: `Trading is now ${data.enabled ? 'enabled' : 'disabled'}`,
          type: 'info'
        });
        setMessages(prevMessages => [...prevMessages, `**Trading is now ${data.enabled ? 'enabled' : 'disabled'}**`]);
      } else if (data.type === 'leaderboard') {
        // Display leaderboard
        let leaderboardMessage = "**Final Leaderboard**\n";
        data.leaderboard.forEach((entry, index) => {
          leaderboardMessage += `${index + 1}. ${entry.userId}: $${entry.profit.toFixed(2)}\n`;
        });
        setMessages(prevMessages => [...prevMessages, leaderboardMessage]);
        setToast({
          show: true,
          message: "Exchange has been settled - check the leaderboard!",
          type: 'info'
        });
      } else if (data.type === 'productVisibility') {
        if (data.visible) {
          setVisibleProducts(prev => 
            prev.includes(data.product) ? prev : [...prev, data.product]
          );
        } else {
          setVisibleProducts(prev => 
            prev.filter(product => product !== data.product)
          );
          
          // If the hidden product is currently active, switch to another visible product
          if (data.product === activeProduct) {
            setVisibleProducts(prev => {
              const otherProducts = prev.filter(p => p !== data.product);
              if (otherProducts.length > 0) {
                setActiveProduct(otherProducts[0]);
              }
              return otherProducts;
            });
          }
        }
      } else if (data.type !== 'orderBookDelta') {
        setMessages(prevMessages => [...prevMessages, event.data]);
      }
    };

    newSocket.onclose = (event) => {
      if (event.wasClean) {
        // console.log(`Connection closed cleanly, code=${event.code} reason=${event.reason}`);
      } else {
        // console.log('Connection died');
      }
      setStatus('Disconnected');
    };

    newSocket.onerror = (error) => {
      // console.log(`WebSocket error: ${error.message}`);
    };

    setSocket(newSocket);
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [connect]);

  useEffect(() => {
    // Only update price if it's a market order
    if (orderForm.type === '0' && activeProduct && orderBooks[activeProduct]) {
      const currentBook = orderBooks[activeProduct];
      const bestAsk = currentBook.asks[0]?.price;
      const bestBid = currentBook.bids[0]?.price;
      
      setOrderForm(prevForm => ({
        ...prevForm,
        price: prevForm.side === '0' 
          ? (bestAsk || '') // Buy - use lowest ask
          : (bestBid || '') // Sell - use highest bid
      }));
    }
  }, [orderBooks, activeProduct, orderForm.type, orderForm.side]); // Add type and side as dependencies

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderForm(prevForm => {
      const newForm = {
        ...prevForm,
        [name]: value
      };

      // If switching to market order, automatically set price based on order book
      if (name === 'type' && value === '0') {
        if (newForm.side === '0') { // Buy order - use lowest ask
          newForm.price = orderBooks[activeProduct]?.asks[0]?.price || '';
        } else { // Sell order - use highest bid
          newForm.price = orderBooks[activeProduct]?.bids[0]?.price || '';
        }
      }
      
      // If switching sides for market order, update price accordingly
      if (name === 'side' && newForm.type === '0') {
        if (value === '0') { // Switched to Buy
          newForm.price = orderBooks[activeProduct]?.asks[0]?.price || '';
        } else { // Switched to Sell
          newForm.price = orderBooks[activeProduct]?.bids[0]?.price || '';
        }
      }

      return newForm;
    });
  };

  const handleProductChange = (product) => {
    // Only allow switching to visible products
    if (visibleProducts.includes(product)) {
      setActiveProduct(product);
      setOrderForm(prev => ({
        ...prev,
        product,
        price: ''
      }));
    }
  };

  const sendOrder = (e) => {
    e.preventDefault();
    if (socket && activeProduct) {
      const order = {
        id: Math.floor(Math.random() * 2147483648), // Random 32-bit integer
        side: parseInt(orderForm.side),
        type: parseInt(orderForm.type),
        price: Math.floor(parseFloat(orderForm.price)),
        quantity: Math.floor(parseInt(orderForm.quantity)),
        product: activeProduct
      };
      socket.send(JSON.stringify(order));
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (socket) {
      const authMessage = {
        type: 'auth',
        username: loginForm.username,
        password: loginForm.password
      };
      // Store the password before sending auth request
      setAdminPassword(loginForm.password);
      socket.send(JSON.stringify(authMessage));

      // Store credentials in cookie when sending login
      Cookies.set('credentials', JSON.stringify({
        username: loginForm.username,
        password: loginForm.password,
        isAdmin: false  // This will be updated when we receive authSuccess
      }), { expires: 7 });
    }
  };

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogout = () => {
    Cookies.remove('credentials');
    setIsAuthenticated(false);
    setIsAdmin(false);
    setAdminPassword('');
    setLoginForm({ username: '', password: '' });
    setCurrentUsername('');
    
    // Reconnect to reset the connection
    if (socket) {
      socket.close();
    }
    connect();
  };

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return (
      <div className="login-container">
        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ show: false, message: '', type: 'info' })}
          />
        )}
        <div className="login-box">
          <h1>Quant Illinois Trading Competition</h1>
          <form onSubmit={handleLogin}>
            <div className="login-form-group">
              <input
                type="text"
                name="username"
                value={loginForm.username}
                onChange={handleLoginInputChange}
                placeholder="Username"
                required
              />
            </div>
            <div className="login-form-group">
              <input
                type="password"
                name="password"
                value={loginForm.password}
                onChange={handleLoginInputChange}
                placeholder="Password"
                required
              />
            </div>
            <button type="submit">Login</button>
          </form>
          <div className={`status-indicator-login status-indicator ${status === 'Connected' ? 'connected' : ''}`}>
              {status}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'info' })}
        />
      )}
      <div className="header">
        <h1>Quant Illinois Trading Competition</h1>
        <div className="status-container">
          <div className={`status-indicator ${status === 'Connected' ? 'connected' : ''}`}>
            {status}
          </div>
          <label className="theme-switch">
            <input
              type="checkbox"
              checked={isDarkMode}
              onChange={toggleTheme}
            />
            <span className="theme-switch-slider"></span>
          </label>
          <button onClick={handleLogout} className="logout-button">
            Logout of {currentUsername}
          </button>
        </div>
      </div>
      
      <div className="main-layout">
        <div className="left-panel">
          <div className="product-tabs">
            {visibleProducts.map(product => (
              <button
                key={product}
                className={`tab ${activeProduct === product ? 'active' : ''}`}
                onClick={() => handleProductChange(product)}
              >
                {product}
              </button>
            ))}
          </div>
          {activeProduct && (
            <OrderBook orders={orderBooks[activeProduct]} product={activeProduct} />
          )}
        </div>
        
        <div className="right-panel">
          <form onSubmit={sendOrder} className="order-form">
            <div className="form-group radio-group">
              <div className="radio-label">Side:</div>
              <div className="radio-options">
                <label className={`radio-option ${orderForm.side === '0' ? 'active' : ''}`} htmlFor="side-buy">
                  <input
                    type="radio"
                    name="side"
                    id="side-buy"
                    value="0"
                    checked={orderForm.side === '0'}
                    onChange={handleInputChange}
                  />
                  <span className="radio-circle"></span>
                  <span className="radio-text">Buy</span>
                </label>
                <label className={`radio-option ${orderForm.side === '1' ? 'active' : ''}`} htmlFor="side-sell">
                  <input
                    type="radio"
                    name="side"
                    id="side-sell"
                    value="1"
                    checked={orderForm.side === '1'}
                    onChange={handleInputChange}
                  />
                  <span className="radio-circle"></span>
                  <span className="radio-text">Sell</span>
                </label>
              </div>
            </div>
            <div className="form-group radio-group">
              <div className="radio-label">Type:</div>
              <div className="radio-options">
                <label className={`radio-option ${orderForm.type === '1' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="type"
                    value="1"
                    checked={orderForm.type === '1'}
                    onChange={handleInputChange}
                  />
                  <span className="radio-circle"></span>
                  <span className="radio-text">Limit</span>
                </label>
                <label className={`radio-option ${orderForm.type === '0' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="type"
                    value="0"
                    checked={orderForm.type === '0'}
                    onChange={handleInputChange}
                  />
                  <span className="radio-circle"></span>
                  <span className="radio-text">Market</span>
                </label>
              </div>
            </div>
            <div className="form-group">
              <input
                type="number"
                name="price"
                value={orderForm.price}
                onChange={handleInputChange}
                placeholder="Price"
                disabled={orderForm.type === '0'}
                step="1"
                min="1"
              />
            </div>
            <div className="form-group">
              <input
                type="number"
                name="quantity"
                value={orderForm.quantity}
                onChange={handleInputChange}
                placeholder="Quantity"
                step="1"
                min="1"
              />
            </div>
            <div className="form-group">
              <select 
                name="product" 
                value={activeProduct || ''} 
                onChange={(e) => handleProductChange(e.target.value)}
                required
              >
                {visibleProducts.map(product => (
                  <option key={product} value={product}>
                    {product}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <button type="submit" disabled={!tradingEnabled}>
                Place Order
              </button>
            </div>
          </form>

          <div className="pnl">
            <h2>Profit & Loss</h2>
            <div className={`value ${pnl >= 0 ? 'positive' : 'negative'}`}>
              ${pnl.toFixed(2)}
            </div>
          </div>

          <UserOrders 
            filledOrders={filledOrders} 
            pendingOrders={pendingOrders} 
            socket={socket}
            positions={positions}
            averageCosts={averageCosts}
            products={products}
            activeProduct={activeProduct}
            visibleProducts={visibleProducts}
          />
          
          <div className="messages">
            <h2>Messages</h2>
            {messages.map((message, index) => (
              <p key={index} dangerouslySetInnerHTML={{ 
                __html: message.startsWith('**') ? 
                  `<strong>${message.slice(2, -2)}</strong>` : 
                  message 
              }} />
            ))}
          </div>

          <AdminPanel 
            socket={socket} 
            userId={loginForm.username}
            isAdmin={isAdmin}
            adminPassword={adminPassword}
            tradingEnabled={tradingEnabled}
            products={products}
          />
        </div>
      </div>
    </div>
  );
}

export default App;